/**
 * Cliente da API (plano, seção 18). Os tipos vêm de `generated.ts`,
 * gerado a partir de `api/openapi.yaml` (`pnpm --filter @lindeza/core gen:api`).
 *
 * Não usa nada de navegador: recebe `fetch` por parâmetro (navegador, service worker,
 * Node e React Native têm um).
 */
import type { Professional } from '../schemas/entities';
import type { OutboxEntry } from '../storage/storage';
import type { components } from './generated';

type Schemas = components['schemas'];
export type ChangeResult = Schemas['ChangeResult'];
export type PublicProfile = Schemas['PublicProfile'];
export type PublicAppointment = Schemas['PublicAppointment'];
export type DaySlotsDto = Schemas['DaySlots'];
export type BillingState = Schemas['BillingState'];
export type PushSubscriptionDto = Schemas['PushSubscription'];

export interface PullResponse {
  changes: Array<{ entity: OutboxEntry['entity']; record: Record<string, unknown> }>;
  cursor: string;
  has_more: boolean;
}

export interface PushResponse {
  results: ChangeResult[];
}

export interface MyAppointments {
  client: { name: string };
  upcoming: PublicAppointment[];
  last: PublicAppointment | null;
}

export interface BookInput {
  /** os serviços escolhidos, na ordem; o atendimento dura e custa a soma deles */
  service_ids?: string[];
  /** formato antigo, um serviço só */
  service_id?: string;
  start: string;
  name: string;
  whatsapp: string;
  location?: 'studio' | 'home';
  address?: string | null;
  notes?: string | null;
  code?: string | null;
  website?: string | null;
  captcha_token?: string | null;
}

/** Resposta mínima de fetch que usamos (compatível com o fetch do navegador e do Node). */
export interface FetchResponseLike {
  ok: boolean;
  status: number;
  headers: { get(name: string): string | null };
  json(): Promise<unknown>;
  text(): Promise<string>;
}

export type FetchLike = (
  input: string,
  init?: { method?: string; headers?: Record<string, string>; body?: string; signal?: unknown },
) => Promise<FetchResponseLike>;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code: string | null = null,
    readonly errors: Record<string, string[]> | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Sem rede, servidor fora do ar ou CORS: tratar como "sem internet". */
  get isNetwork(): boolean {
    return this.status === 0;
  }
}

export interface ApiClientOptions {
  /** ex.: "" (mesma origem) ou "https://app.lindeza.app" */
  baseUrl: string;
  fetch: FetchLike;
  getToken?: () => string | null | Promise<string | null>;
  /** tempo máximo por requisição */
  timeoutMs?: number;
}

export class ApiClient {
  constructor(private readonly opts: ApiClientOptions) {}

  get baseUrl(): string {
    return this.opts.baseUrl.replace(/\/$/, '');
  }

  url(path: string): string {
    return `${this.baseUrl}/api/v1${path}`;
  }

  async request<T>(method: string, path: string, body?: unknown, auth = true): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (auth && this.opts.getToken) {
      const token = await this.opts.getToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    }
    let res: FetchResponseLike;
    try {
      res = await withTimeout(
        this.opts.fetch(this.url(path), {
          method,
          headers,
          ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
        }),
        this.opts.timeoutMs ?? 20_000,
      );
    } catch {
      throw new ApiError(0, 'Sem conexão com a internet.');
    }
    if (res.status === 204) return undefined as T;
    let payload: unknown = null;
    const type = res.headers.get('content-type') ?? '';
    if (type.includes('json')) {
      try {
        payload = await res.json();
      } catch {
        payload = null;
      }
    }
    if (!res.ok) {
      const p = (payload ?? {}) as { message?: string; code?: string; errors?: Record<string, string[]> };
      throw new ApiError(res.status, p.message ?? defaultMessage(res.status), p.code ?? null, p.errors ?? null);
    }
    return payload as T;
  }

  // ------------------------------------------------------------ autenticação

  requestMagicLink(email: string, name?: string) {
    return this.request<{ sent: boolean; debug_code?: string; debug_token?: string }>(
      'POST',
      '/auth/magic-link',
      { email, ...(name ? { name } : {}) },
      false,
    );
  }

  /** Cadastro com senha: cria a conta e já devolve a sessão. */
  register(input: { name: string; email: string; password: string; source?: string; campaign?: string; ref?: string }, deviceName?: string) {
    return this.request<{ token: string; professional: Professional }>(
      'POST',
      '/auth/register',
      { ...input, ...(deviceName ? { device_name: deviceName } : {}) },
      false,
    );
  }

  /** Entrada por senha. O erro é o mesmo para e-mail errado e senha errada. */
  loginWithPassword(email: string, password: string, deviceName?: string) {
    return this.request<{ token: string; professional: Professional }>(
      'POST',
      '/auth/password',
      { email, password, ...(deviceName ? { device_name: deviceName } : {}) },
      false,
    );
  }

  verify(input: { token: string } | { email: string; code: string }, deviceName?: string) {
    return this.request<{ token: string; professional: Professional }>(
      'POST',
      '/auth/verify',
      { ...input, ...(deviceName ? { device_name: deviceName } : {}) },
      false,
    );
  }

  logout() {
    return this.request<void>('POST', '/auth/logout');
  }

  logoutAll() {
    return this.request<void>('POST', '/auth/logout-all');
  }

  // ------------------------------------------------------------ perfil

  me() {
    return this.request<{ professional: Professional }>('GET', '/me');
  }

  updateMe(patch: Partial<Professional>) {
    return this.request<{ professional: Professional }>('PATCH', '/me', patch);
  }

  setSlug(slug: string) {
    return this.request<{ professional: Professional }>('PUT', '/me/slug', { slug });
  }

  checkSlug(slug: string) {
    return this.request<{ slug: string; available: boolean; suggestion?: string | null }>(
      'GET',
      `/slug-check?slug=${encodeURIComponent(slug)}`,
    );
  }

  regenerateAccessCode(clientId: string) {
    return this.request<{ data: Record<string, unknown> }>('POST', `/clients/${clientId}/access-code`);
  }

  exportData() {
    return this.request<Record<string, unknown>>('GET', '/me/export');
  }

  // ------------------------------------------------------------ sincronização

  push(changes: OutboxEntry[], deviceId?: string) {
    return this.request<PushResponse>('POST', '/sync/push', {
      ...(deviceId ? { device_id: deviceId } : {}),
      changes: changes.map((c) => ({
        op_id: c.op_id,
        entity: c.entity,
        id: c.id,
        op: c.op,
        client_updated_at: c.client_updated_at,
        base_version: c.base_version,
        data: c.data,
      })),
    });
  }

  pull(since: string, limit = 500) {
    return this.request<PullResponse>('GET', `/sync/pull?since=${encodeURIComponent(since)}&limit=${limit}`);
  }

  // ------------------------------------------------------------ push

  vapidKey() {
    return this.request<{ public_key: string | null }>('GET', '/push/vapid-key', undefined, false);
  }

  pushSubscribe(subscription: PushSubscriptionDto) {
    return this.request<void>('POST', '/push/subscribe', { subscription });
  }

  pushUnsubscribe(endpoint: string) {
    return this.request<void>('DELETE', '/push/subscribe', { endpoint });
  }

  // ------------------------------------------------------------ assinatura

  billing() {
    return this.request<BillingState>('GET', '/billing');
  }

  checkout(plan: 'essencial' | 'estetica', method: 'pix' | 'card', cycle: 'monthly' | 'yearly' = 'monthly') {
    return this.request<{
      status: 'redirect' | 'pix' | 'active';
      checkout_url?: string | null;
      pix_code?: string | null;
      pix_qr_base64?: string | null;
      professional?: Professional;
    }>('POST', '/billing/checkout', { plan, method, cycle });
  }

  // ------------------------------------------------------------ público (cliente)

  publicProfile(slug: string) {
    return this.request<PublicProfile>('GET', `/p/${enc(slug)}`, undefined, false);
  }

  slots(
    slug: string,
    params: {
      /** ids separados por vírgula, na ordem escolhida */
      services?: string;
      /** formato antigo, um serviço só */
      service?: string;
      from?: string;
      to?: string;
      location?: 'studio' | 'home';
      ignore?: string;
      code?: string;
    },
  ) {
    const q = new URLSearchParamsLite(params).toString();
    return this.request<{ timezone: string; days: DaySlotsDto[] }>('GET', `/p/${enc(slug)}/slots?${q}`, undefined, false);
  }

  book(slug: string, input: BookInput) {
    return this.request<{ appointment: PublicAppointment; code: string }>('POST', `/p/${enc(slug)}/book`, input, false);
  }

  myAppointments(slug: string, code: string) {
    return this.request<MyAppointments>('GET', `/p/${enc(slug)}/me?code=${enc(code)}`, undefined, false);
  }

  cancelMine(slug: string, code: string, appointmentId: string) {
    return this.request<{ appointment: PublicAppointment }>(
      'POST',
      `/p/${enc(slug)}/me/${enc(appointmentId)}/cancel`,
      { code },
      false,
    );
  }

  rescheduleMine(slug: string, code: string, appointmentId: string, start: string) {
    return this.request<{ appointment: PublicAppointment }>(
      'POST',
      `/p/${enc(slug)}/me/${enc(appointmentId)}/reschedule`,
      { code, start },
      false,
    );
  }

  forgetMe(slug: string, code: string) {
    return this.request<void>('POST', `/p/${enc(slug)}/me/forget`, { code }, false);
  }

  clientPushSubscribe(slug: string, code: string, subscription: PushSubscriptionDto) {
    return this.request<void>('POST', `/p/${enc(slug)}/push/subscribe`, { code, subscription }, false);
  }

  manifestUrl(slug: string, code?: string | null): string {
    return this.url(`/p/${enc(slug)}/manifest.webmanifest${code ? `?code=${enc(code)}` : ''}`);
  }

  iconUrl(slug: string, size: 180 | 192 | 512): string {
    return this.url(`/p/${enc(slug)}/icon-${size}.png`);
  }

  icsUrl(slug: string, appointmentId: string, code: string): string {
    return this.url(`/p/${enc(slug)}/${enc(appointmentId)}.ics?code=${enc(code)}`);
  }
}

function enc(v: string): string {
  return encodeURIComponent(v);
}

/** URLSearchParams mínimo (não depende do DOM). */
class URLSearchParamsLite {
  constructor(private readonly params: Record<string, string | undefined>) {}
  toString(): string {
    return Object.entries(this.params)
      .filter(([, v]) => v !== undefined && v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v as string)}`)
      .join('&');
  }
}

function defaultMessage(status: number): string {
  if (status === 401) return 'Sua sessão expirou. Entre de novo.';
  if (status === 403) return 'Sem permissão.';
  if (status === 404) return 'Não encontrado.';
  if (status === 409) return 'Esse horário acabou de ser reservado. Escolha outro.';
  if (status === 422) return 'Confira os dados.';
  if (status === 429) return 'Muitas tentativas. Espere um pouco e tente de novo.';
  if (status >= 500) return 'O servidor teve um problema. Tente de novo em instantes.';
  return 'Algo deu errado.';
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  const g = globalThis as unknown as {
    setTimeout?: (fn: () => void, ms: number) => unknown;
    clearTimeout?: (id: unknown) => void;
  };
  if (!g.setTimeout) return promise;
  return new Promise<T>((resolve, reject) => {
    const id = g.setTimeout!(() => reject(new Error('timeout')), ms);
    promise.then(
      (v) => {
        g.clearTimeout?.(id);
        resolve(v);
      },
      (e: unknown) => {
        g.clearTimeout?.(id);
        reject(e);
      },
    );
  });
}
